// Core
import PropTypes from "prop-types";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import { FieldSearchSelect, Heatmap, LineChart, SectionCard } from "components/AtomsV2";

// Redux
import { useSelector } from "react-redux";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { selectTimeSelector } from "state/application/applicationSlice";
import { useGetBilateralPricesByProviderQuery } from "state/bilateralPrices/bilateralPricesApiSlice";

// Utils
import { DateTime } from "luxon";
import { useGetMembers } from "utils/crud/read";

// One CSS-var colour per receiver series (theme-aware, resolved by LineChart).
const SERIES_COLORS = [
  "var(--accent)",
  "var(--warm)",
  "var(--info)",
  "var(--success)",
  "var(--warning)",
  "var(--danger)",
];

// 7 days x 24 hours heatmap scaffolding.
const DAY_KEYS = [1, 2, 3, 4, 5, 6, 7]; // luxon weekday: 1 = Monday … 7 = Sunday

function BilateralPricesCharts({ providerUserId, setProviderUserId }) {
  const { t } = useTranslation(["community"]);
  const community = useSelector(selectCurrentCommunity);
  const { startDate, endDate } = useSelector(selectTimeSelector);

  const members = useGetMembers("true") ?? [];
  const userIdToName = (id) => members.find((m) => m.id === id)?.name ?? id?.slice(0, 8);
  const memberOptions = members.map((m) => ({ value: m.id, label: m.name }));

  // Auto-select first member as provider.
  useEffect(() => {
    if (!providerUserId && members.length > 0) {
      setProviderUserId(members[0].id);
    }
  }, [members.length]);

  const {
    data: bpData,
    isFetching,
    isError,
  } = useGetBilateralPricesByProviderQuery(
    { communityId: community.id, providerUserId, startDate, endDate },
    { skip: !providerUserId }
  );

  const rows = useMemo(() => bpData ?? [], [bpData]);
  const hasData = !isFetching && !isError && rows.length > 0;
  const providerName = userIdToName(providerUserId);

  // Line chart — one series per receiver, plotted on a real datetime axis.
  const { series, timestamps, labels } = useMemo(() => {
    const ts = [...new Set(rows.map((d) => d.datetime_timestamp))].sort();
    const tsIndex = new Map(ts.map((value, i) => [value, i]));
    const receiverIds = [...new Set(rows.map((d) => d.receiver_user_id))];
    const built = receiverIds.map((receiverId, idx) => {
      const data = new Array(ts.length).fill(null);
      rows
        .filter((d) => d.receiver_user_id === receiverId)
        .forEach((d) => {
          data[tsIndex.get(d.datetime_timestamp)] = d.price != null ? +d.price.toFixed(4) : null;
        });
      return {
        name: userIdToName(receiverId),
        data,
        color: SERIES_COLORS[idx % SERIES_COLORS.length],
      };
    });
    return {
      series: built,
      timestamps: ts,
      labels: ts.map((value) => DateTime.fromISO(value).toFormat("dd/MM HH:mm")),
    };
  }, [rows, members]);

  // Heatmap — average price per weekday x hour across all receivers.
  const { matrix, dayLabels, hourLabels } = useMemo(() => {
    const sum = DAY_KEYS.map(() => new Array(24).fill(0));
    const count = DAY_KEYS.map(() => new Array(24).fill(0));
    rows.forEach((d) => {
      if (d.price == null) return;
      const dt = DateTime.fromISO(d.datetime_timestamp);
      const dRow = dt.weekday - 1; // 0..6
      const hCol = dt.hour; // 0..23
      sum[dRow][hCol] += d.price;
      count[dRow][hCol] += 1;
    });
    const m = sum.map((row, r) => row.map((s, c) => (count[r][c] ? s / count[r][c] : null)));
    return {
      matrix: m,
      dayLabels: t("bilateralPrices.charts.heatmap_days", { returnObjects: true }),
      hourLabels: Array.from({ length: 24 }, (_, h) => (h % 3 === 0 ? `${h}h` : "")),
    };
  }, [rows, t]);

  const providerSelect = (
    <div className="min-w-[220px]">
      <FieldSearchSelect
        options={memberOptions}
        value={providerUserId ?? ""}
        onChange={setProviderUserId}
        label={t("bilateralPrices.charts.provider_label")}
      />
    </div>
  );

  return (
    <div className="flex flex-col gap-rec">
      <SectionCard
        id="bilateral-line"
        icon="show_chart"
        title={
          hasData
            ? `${t("bilateralPrices.charts.title")} — ${providerName}`
            : t("bilateralPrices.charts.title")
        }
        action={providerSelect}
      >
        {hasData ? (
          <LineChart
            series={series}
            labels={labels}
            timestamps={timestamps}
            unit="€"
            height={320}
            type="area"
            yTitle="€/kWh"
            showLegend
          />
        ) : (
          <div className="py-10 text-center text-[13px] text-ink-3">
            {isFetching ? "…" : t("bilateralPrices.charts.no_data")}
          </div>
        )}
      </SectionCard>

      <SectionCard
        id="bilateral-heatmap"
        icon="grid_on"
        title={t("bilateralPrices.charts.heatmap_title")}
      >
        {hasData ? (
          <Heatmap rowLabels={dayLabels} colLabels={hourLabels} matrix={matrix} unit=" €" />
        ) : (
          <div className="py-10 text-center text-[13px] text-ink-3">
            {isFetching ? "…" : t("bilateralPrices.charts.no_data")}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

BilateralPricesCharts.propTypes = {
  providerUserId: PropTypes.string.isRequired,
  setProviderUserId: PropTypes.func.isRequired,
};

export default BilateralPricesCharts;
